import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

export default function AdminMembers() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState({ user_id: "", plan_type: "basic", status: "active", monthly_quota_hours: 40, renewal_date: "" });

  useEffect(() => { if (!isAdmin) navigate("/login"); else loadData(); }, [isAdmin]);

  const loadData = async () => {
    try {
      const res = await api("get", "/members");
      setMembers(res.data);
    } catch { toast.error("Failed to load members"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingMember) {
        await api("put", `/members/${editingMember.id}`, {
          plan_type: form.plan_type,
          status: form.status,
          monthly_quota_hours: parseFloat(form.monthly_quota_hours),
          renewal_date: form.renewal_date || undefined,
        });
        toast.success("Member updated");
      } else {
        await api("post", "/members", {
          user_id: form.user_id,
          plan_type: form.plan_type,
          status: form.status,
          monthly_quota_hours: parseFloat(form.monthly_quota_hours),
          renewal_date: form.renewal_date || undefined,
        });
        toast.success("Member created");
      }
      setDialogOpen(false);
      setEditingMember(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (m) => {
    setEditingMember(m);
    setForm({ user_id: m.user_id, plan_type: m.plan_type, status: m.status, monthly_quota_hours: m.monthly_quota_hours, renewal_date: m.renewal_date || "" });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api("delete", `/members/${id}`);
      toast.success("Member removed");
      loadData();
    } catch { toast.error("Failed to delete"); }
  };

  const openNew = () => {
    setEditingMember(null);
    setForm({ user_id: "", plan_type: "basic", status: "active", monthly_quota_hours: 40, renewal_date: "" });
    setDialogOpen(true);
  };

  if (loading) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-members">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Members</h1>
          <p className="text-muted-foreground mt-1">Manage coworking space memberships</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-foreground text-background rounded-none" data-testid="add-member-btn">
              <Plus className="h-4 w-4 mr-2" />Add Member
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="member-dialog">
            <DialogHeader>
              <DialogTitle>{editingMember ? "Edit Member" : "Add New Member"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {!editingMember && (
                <div>
                  <Label>User ID</Label>
                  <Input value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} placeholder="Enter user ID" data-testid="member-user-id-input" />
                  <p className="text-xs text-muted-foreground mt-1">Paste the user's ID from the users list</p>
                </div>
              )}
              <div>
                <Label>Plan Type</Label>
                <Select value={form.plan_type} onValueChange={v => setForm({ ...form, plan_type: v })}>
                  <SelectTrigger data-testid="member-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="member-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monthly Quota (hours)</Label>
                <Input type="number" value={form.monthly_quota_hours} onChange={e => setForm({ ...form, monthly_quota_hours: e.target.value })} data-testid="member-quota-input" />
              </div>
              <div>
                <Label>Renewal Date</Label>
                <Input type="date" value={form.renewal_date} onChange={e => setForm({ ...form, renewal_date: e.target.value })} data-testid="member-renewal-input" />
              </div>
              <Button onClick={handleSave} className="w-full bg-foreground text-background rounded-none" data-testid="save-member-btn">
                {editingMember ? "Update Member" : "Create Member"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border overflow-x-auto" data-testid="members-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Quota</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Renewal</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map(m => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.user_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{m.user_email}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{m.plan_type}</Badge></TableCell>
                <TableCell><Badge className={m.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{m.status}</Badge></TableCell>
                <TableCell>{m.monthly_quota_hours}h</TableCell>
                <TableCell>{m.used_hours?.toFixed(1)}h</TableCell>
                <TableCell className="font-mono text-xs">{m.renewal_date || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(m)} data-testid={`edit-member-${m.id}`}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(m.id)} data-testid={`delete-member-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {members.length === 0 && <div className="p-12 text-center text-muted-foreground">No members found</div>}
      </div>
    </div>
  );
}
